import _ from 'lodash';
import { useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CircularProgress, Menu, MenuItem, Tooltip } from '@mui/material';
import moment from 'moment';
import { ReactComponent as DownloadIcon } from '@/img/bookings/download.svg';
import { ReactComponent as DownloadIconBlack } from '@/img/bookings/download-b.svg';
import { CSV_EXPORT_TYPES } from 'arbitrip-common/general/constants';
import BookingsActions from '@/actions/BookingsActions';
import Analytics from 'arbitrip-common/client/analytics';
import BookingsConstants from '@/constants/BookingsConstants';
import MaterialDesignDialog from '@/components/general/MaterialDesignDialog.react';

const DIALOG_DATE_FORMAT = 'DD/MM/YYYY';

const ExportButton = ({ profile, disabled, downloadStatus, activeFilters, activeTab }) => {
	const { t } = useTranslation();
	const [exportAnchorEl, setExportAnchorEl] = useState(null);
	const [showConfirmDialog, setShowConfirmDialog] = useState(false);
	const [pendingExportType, setPendingExportType] = useState(null);

	const exportButtonRef = useRef(null);

	const priority_export_enabled = useMemo(() => {
		return _.get(profile, 'company.settings', false);
	}, [profile]);

	const onExportClick = (e) => {
		setExportAnchorEl(e.currentTarget);
	};

	const onExportClose = () => {
		setExportAnchorEl(null);
	};

	const onDownloadBookingsCsvClick = (exportType) => {
		const company_id = _.get(profile, 'company.id');
		const is_agent = _.get(profile, 'agent') || _.get(profile, 'super_agent');

		BookingsActions.downloadedBookingsCsv(is_agent, activeFilters, exportType, activeTab);
		Analytics.actions.interactions.downloadedBookingsCsv(company_id, is_agent, activeFilters);
	};

	const openConfirmDialog = (exportType) => {
		setPendingExportType(exportType);
		setShowConfirmDialog(true);
	};

	const onConfirmExport = () => {
		setShowConfirmDialog(false);
		onDownloadBookingsCsvClick(pendingExportType);
	};

	const onCancelExport = () => {
		setShowConfirmDialog(false);
		setPendingExportType(null);
	};

	const onExportMenuChoice = (exportType) => {
		onExportClose();
		openConfirmDialog(exportType);
	};

	const isBusy = () => downloadStatus === BookingsConstants.STATUS.BUSY;

	const dialogContent = useMemo(() => {
		const filters = activeFilters || {};
		const hasCheckInFilter = filters.after_date || filters.before_date;
		const hasCancellationFilter = filters.cancellation_deadline_after || filters.cancellation_deadline_before;

		const makeDateRange = (start, end) => (
			<strong>
				<span dir="ltr">
					{start} – {end}
				</span>
			</strong>
		);

		if (hasCheckInFilter) {
			const start = filters.after_date ? moment(filters.after_date).format(DIALOG_DATE_FORMAT) : '...';
			const end = filters.before_date ? moment(filters.before_date).format(DIALOG_DATE_FORMAT) : '...';
			return (
				<span>
					{t('Bookings.Export.WithCheckInFilterPrefix')} {makeDateRange(start, end)}.
				</span>
			);
		}

		if (hasCancellationFilter) {
			const start = filters.cancellation_deadline_after
				? moment(filters.cancellation_deadline_after).format(DIALOG_DATE_FORMAT)
				: '...';
			const end = filters.cancellation_deadline_before
				? moment(filters.cancellation_deadline_before).format(DIALOG_DATE_FORMAT)
				: '...';
			return (
				<span>
					{t('Bookings.Export.WithCancellationFilterPrefix')} {makeDateRange(start, end)}.
				</span>
			);
		}

		const defaultStart = moment().subtract(1, 'year').format(DIALOG_DATE_FORMAT);
		const defaultEnd = moment().format(DIALOG_DATE_FORMAT);
		return (
			<span>
				{t('Bookings.Export.NoFilterPrefix')} {makeDateRange(defaultStart, defaultEnd)}
				{t('Bookings.Export.NoFilterSuffix')}
			</span>
		);
	}, [activeFilters, t, showConfirmDialog]);

	const tooltipTitle = useMemo(() => {
		const dateFormat = 'DD/MM/YYYY';

		if (activeFilters.after_date || activeFilters.before_date) {
			const start = activeFilters.after_date ? moment(activeFilters.after_date).format(dateFormat) : '...';
			const end = activeFilters.before_date ? moment(activeFilters.before_date).format(dateFormat) : '...';
			return `Export reservations - Check-in: ${start} - ${end}`;
		}

		if (activeFilters.cancellation_deadline_after || activeFilters.cancellation_deadline_before) {
			const start = activeFilters.cancellation_deadline_after
				? moment(activeFilters.cancellation_deadline_after).format(dateFormat)
				: '...';
			const end = activeFilters.cancellation_deadline_before
				? moment(activeFilters.cancellation_deadline_before).format(dateFormat)
				: '...';
			return `Export reservations - Deadline: ${start} - ${end}`;
		}

		return `Export reservations - Creation Date: ${moment().subtract(1, 'year').format(dateFormat)} - ${moment().format(
			dateFormat,
		)}`;
	}, [activeFilters]);

	return (
		<>
			<Tooltip title={tooltipTitle}>
				<div
					tabIndex={0}
					role="button"
					aria-disabled={disabled || isBusy()}
					aria-controls="dropdown-menu"
					aria-haspopup="true"
					onClick={(e) => {
						if (!disabled && !isBusy()) {
							priority_export_enabled ? onExportClick(e) : openConfirmDialog(CSV_EXPORT_TYPES.CSV);
						}
					}}
					ref={exportButtonRef}
					onKeyDown={(e) => {
						if (!disabled && !isBusy() && (e.key === 'Enter' || e.key === ' ')) {
							e.preventDefault();
							priority_export_enabled ? onExportClick(e) : openConfirmDialog(CSV_EXPORT_TYPES.CSV);
						}
					}}
					style={{
						cursor: disabled || isBusy() ? 'not-allowed' : 'pointer',
						display: 'flex',
						alignItems: 'center',
					}}
				>
					{isBusy() ? (
						<div className="busy-indicator">
							<CircularProgress size={16} />
						</div>
					) : (
						<DownloadIcon />
					)}
				</div>
			</Tooltip>
			{priority_export_enabled && (
				<Menu
					id="csv-dropdown-menu"
					anchorEl={exportAnchorEl}
					open={Boolean(exportAnchorEl)}
					onClose={onExportClose}
					anchorOrigin={{
						vertical: 'bottom',
						horizontal: 'right',
					}}
					transformOrigin={{
						vertical: 'top',
						horizontal: 'right',
					}}
				>
					<MenuItem onClick={() => onExportMenuChoice(CSV_EXPORT_TYPES.XLSX)}>
						<DownloadIconBlack className="menu-item-icon" /> {t('Bookings.Export.Excel')}
					</MenuItem>
					<MenuItem onClick={() => onExportMenuChoice(CSV_EXPORT_TYPES.CSV)}>
						<DownloadIconBlack className="menu-item-icon" /> {t('Bookings.Export.CSV')}
					</MenuItem>
					<MenuItem onClick={() => onExportMenuChoice(CSV_EXPORT_TYPES.PRIORITY)}>
						<DownloadIconBlack className="menu-item-icon" /> {t('Bookings.Export.Priority')}
					</MenuItem>
				</Menu>
			)}
			<MaterialDesignDialog
				open={showConfirmDialog}
				onClose={onCancelExport}
				onKeyDown={(e) => e.key === 'Escape' && onCancelExport()}
				title={t('Bookings.Export.ConfirmTitle')}
				content={dialogContent}
				actions={[
					{ text: t('Bookings.Export.CancelButton'), onClick: onCancelExport },
					{ text: t('Bookings.Export.ConfirmButton'), onClick: onConfirmExport },
				]}
			/>
		</>
	);
};

export default ExportButton;
